import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/bionexus/Header";
import Footer from "@/components/Footer";
import BackNav from "@/components/BackNav";
import { useMaterials, useApplications } from "@/hooks/useBioNexusData";
import { 
  useUpdateMaterial, 
  useDeleteMaterial, 
  useToggleMaterialPublished,
  useUpdateApplication,
  useDeleteApplication,
  useBulkPublishMaterials,
  useBulkDeleteMaterials
} from "@/hooks/useAdminMutations";
import { MaterialEditDialog } from "@/components/admin/MaterialEditDialog";
import { ApplicationEditDialog } from "@/components/admin/ApplicationEditDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Edit, Trash2, Check, FileText, Layers } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Material, Application } from "@/hooks/useBioNexusData";
import type { TablesUpdate } from "@/integrations/supabase/types";

type PublishFilter = "all" | "published" | "draft";

const AdminContent = () => {
  const { data: materials } = useMaterials();
  const { data: applications } = useApplications();
  const { toast } = useToast();

  // Filters
  const [materialFilter, setMaterialFilter] = useState<PublishFilter>("all");
  const [appFilter, setAppFilter] = useState<PublishFilter>("all");

  // Selection
  const [selectedMaterials, setSelectedMaterials] = useState<Set<string>>(new Set());
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());

  // Edit dialogs
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [editingApp, setEditingApp] = useState<Application | null>(null);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: "material" | "application"; id: string; name: string } | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<"materials" | null>(null);

  // Mutations
  const updateMaterial = useUpdateMaterial();
  const deleteMaterial = useDeleteMaterial();
  const togglePublished = useToggleMaterialPublished();
  const updateApplication = useUpdateApplication();
  const deleteApplication = useDeleteApplication();
  const bulkPublish = useBulkPublishMaterials();
  const bulkDelete = useBulkDeleteMaterials();

  // Filter materials
  const filteredMaterials = materials?.filter((m) => {
    if (materialFilter === "published") return m.published;
    if (materialFilter === "draft") return !m.published;
    return true;
  });

  // Filter applications (using status for applications)
  const filteredApps = applications?.filter((a) => {
    if (appFilter === "published") return a.status === "approved";
    if (appFilter === "draft") return a.status !== "approved";
    return true;
  });

  // Selection handlers
  const toggleMaterialSelection = (id: string) => {
    setSelectedMaterials((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllMaterials = () => {
    if (selectedMaterials.size === filteredMaterials?.length) {
      setSelectedMaterials(new Set());
    } else {
      setSelectedMaterials(new Set(filteredMaterials?.map((m) => m.id)));
    }
  };

  // Material actions
  const handleSaveMaterial = (id: string, updates: TablesUpdate<"materials">) => {
    updateMaterial.mutate({ id, updates }, {
      onSuccess: () => {
        toast({ title: "Updated ✓", description: "Material has been updated." });
        setEditingMaterial(null);
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    });
  };

  const handleTogglePublished = (id: string, published: boolean) => {
    togglePublished.mutate({ id, published }, {
      onSuccess: () => {
        toast({
          title: published ? "Published" : "Unpublished",
          description: published ? "Now visible on website." : "Hidden from website.",
        });
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    });
  };

  const handleDeleteMaterial = () => {
    if (!deleteConfirm || deleteConfirm.type !== "material") return;
    deleteMaterial.mutate(deleteConfirm.id, {
      onSuccess: () => {
        toast({ title: "Deleted", description: "Material has been removed." });
        setDeleteConfirm(null);
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    });
  };

  // Application actions
  const handleSaveApplication = (id: string, updates: TablesUpdate<"applications">) => {
    updateApplication.mutate({ id, updates }, {
      onSuccess: () => {
        toast({ title: "Updated ✓", description: "Application has been updated." });
        setEditingApp(null);
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    });
  };

  const handleDeleteApplication = () => {
    if (!deleteConfirm || deleteConfirm.type !== "application") return;
    deleteApplication.mutate(deleteConfirm.id, {
      onSuccess: () => {
        toast({ title: "Deleted", description: "Application has been removed." });
        setDeleteConfirm(null);
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    });
  };

  // Bulk actions
  const handleBulkPublish = () => {
    if (selectedMaterials.size === 0) return;
    bulkPublish.mutate(Array.from(selectedMaterials), {
      onSuccess: () => {
        toast({ title: "Published", description: `${selectedMaterials.size} items published.` });
        setSelectedMaterials(new Set());
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    });
  };

  const handleBulkDelete = () => {
    if (selectedMaterials.size === 0) return;
    bulkDelete.mutate(Array.from(selectedMaterials), {
      onSuccess: () => {
        toast({ title: "Deleted", description: `${selectedMaterials.size} items deleted.` });
        setSelectedMaterials(new Set());
        setBulkDeleteConfirm(null);
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    });
  };

  return (
    <div className="min-h-screen bg-background page-enter">
      <Header />
      
      <main className="pt-32 pb-20 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-8">
            <BackNav to="/admin" label="Back to Dashboard" />
          </div>

          <div className="flex items-center justify-between mb-12">
            <div>
              <h1 className="font-display font-medium text-4xl mb-2">Content Management</h1>
              <p className="font-mono text-xs uppercase text-muted-foreground">
                内容管理 — Edit, publish, and manage all content
              </p>
            </div>
            <div className="flex gap-4">
              <Link 
                to="/admin/review"
                className="inline-flex items-center gap-2 px-4 py-2 border border-border font-mono text-sm hover:bg-muted transition-colors"
              >
                <FileText className="w-4 h-4" />
                Review Queue
              </Link>
              <Link 
                to="/admin/materials/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-mono text-sm hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
                Add New
              </Link>
            </div>
          </div>

          {/* Materials Section */}
          <section className="mb-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Materials ({filteredMaterials?.length || 0})
              </h2>
              <div className="flex items-center gap-4">
                <Select value={materialFilter} onValueChange={(v) => setMaterialFilter(v as PublishFilter)}>
                  <SelectTrigger className="w-[150px] font-mono text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Items</SelectItem>
                    <SelectItem value="published">Published Only</SelectItem>
                    <SelectItem value="draft">Drafts Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Bulk actions bar */}
            {selectedMaterials.size > 0 && (
              <div className="flex items-center gap-4 mb-4 p-4 bg-muted border border-border">
                <span className="font-mono text-sm">Selected: {selectedMaterials.size} items</span>
                <Button
                  size="sm"
                  onClick={handleBulkPublish}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Publish Selected
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBulkDeleteConfirm("materials")}
                  className="border-destructive text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Delete Selected
                </Button>
              </div>
            )}

            <div className="border border-border overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="p-4 text-left w-12">
                      <Checkbox
                        checked={selectedMaterials.size === filteredMaterials?.length && filteredMaterials?.length > 0}
                        onCheckedChange={toggleAllMaterials}
                      />
                    </th>
                    <th className="p-4 text-left font-mono text-xs uppercase text-muted-foreground">Name</th>
                    <th className="p-4 text-left font-mono text-xs uppercase text-muted-foreground">Family</th>
                    <th className="p-4 text-left font-mono text-xs uppercase text-muted-foreground">TRL</th>
                    <th className="p-4 text-center font-mono text-xs uppercase text-muted-foreground">Published</th>
                    <th className="p-4 text-right font-mono text-xs uppercase text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMaterials?.map((material) => (
                    <tr key={material.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <Checkbox
                          checked={selectedMaterials.has(material.id)}
                          onCheckedChange={() => toggleMaterialSelection(material.id)}
                        />
                      </td>
                      <td className="p-4">
                        <div>
                          <span className="font-display font-medium">{material.name}</span>
                          {material.name_cn && (
                            <span className="text-muted-foreground ml-2 text-sm">{material.name_cn}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 font-mono text-xs">{material.family || "—"}</td>
                      <td className="p-4 font-mono text-xs">{material.trl_level || "—"}</td>
                      <td className="p-4 text-center">
                        <Checkbox
                          checked={material.published ?? false}
                          onCheckedChange={(checked) => handleTogglePublished(material.id, !!checked)}
                        />
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingMaterial(material)}
                            className="border-primary text-primary hover:bg-primary/10"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteConfirm({ type: "material", id: material.id, name: material.name })}
                            className="border-destructive text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(!filteredMaterials || filteredMaterials.length === 0) && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-muted-foreground font-mono text-sm">
                        No materials found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Applications Section */}
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Applications ({filteredApps?.length || 0})
              </h2>
              <Select value={appFilter} onValueChange={(v) => setAppFilter(v as PublishFilter)}>
                <SelectTrigger className="w-[150px] font-mono text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  <SelectItem value="published">Published Only</SelectItem>
                  <SelectItem value="draft">Drafts Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border border-border overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="p-4 text-left font-mono text-xs uppercase text-muted-foreground">Title</th>
                    <th className="p-4 text-left font-mono text-xs uppercase text-muted-foreground">Category</th>
                    <th className="p-4 text-left font-mono text-xs uppercase text-muted-foreground">Type</th>
                    <th className="p-4 text-center font-mono text-xs uppercase text-muted-foreground">Status</th>
                    <th className="p-4 text-right font-mono text-xs uppercase text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps?.map((app) => (
                    <tr key={app.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <span className="font-display font-medium">{app.title}</span>
                      </td>
                      <td className="p-4 font-mono text-xs">{app.category || "—"}</td>
                      <td className="p-4 font-mono text-xs">{app.application_type || "—"}</td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 font-mono text-xs ${
                          app.status === "approved" 
                            ? "bg-primary/20 text-primary" 
                            : "bg-muted text-muted-foreground"
                        }`}>
                          {app.status === "approved" ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingApp(app)}
                            className="border-primary text-primary hover:bg-primary/10"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteConfirm({ type: "application", id: app.id, name: app.title })}
                            className="border-destructive text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(!filteredApps || filteredApps.length === 0) && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-muted-foreground font-mono text-sm">
                        No applications found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
      
      <Footer />

      {/* Material Edit Dialog */}
      <MaterialEditDialog
        open={!!editingMaterial}
        onOpenChange={(open) => !open && setEditingMaterial(null)}
        material={editingMaterial}
        onSave={handleSaveMaterial}
        isSaving={updateMaterial.isPending}
      />

      {/* Application Edit Dialog */}
      <ApplicationEditDialog
        open={!!editingApp}
        onOpenChange={(open) => !open && setEditingApp(null)}
        application={editingApp}
        onSave={handleSaveApplication}
        isSaving={updateApplication.isPending}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteConfirm?.type}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteConfirm?.name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteConfirm?.type === "material" ? handleDeleteMaterial : handleDeleteApplication}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={!!bulkDeleteConfirm} onOpenChange={(open) => !open && setBulkDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedMaterials.size} items?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all selected materials. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminContent;
